import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, X, Check, Trash2, Palmtree, Timer, Stethoscope, Calendar, ThumbsUp, ThumbsDown, FileDown, FileText } from "lucide-react";
import api from "@/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import AdjustmentsModal from "@/pages/ferie/AdjustmentsModal";
import ResiduiCard from "@/pages/ferie/ResiduiCard";
import NewRequestModal from "@/pages/ferie/NewRequestModal";
import RejectModal from "@/pages/ferie/RejectModal";

const TIPO_META = {
  ferie:     { label: "Ferie",     icon: Palmtree,    color: "#64748b" },
  permesso:  { label: "Permesso",  icon: Timer,       color: "#db2777" },
  malattia:  { label: "Malattia",  icon: Stethoscope, color: "#0891b2" },
};

const STATO_META = {
  in_attesa:  { label: "In attesa",  className: "bg-amber-100 text-amber-800 border-amber-200" },
  approvata:  { label: "Approvata",  className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  rifiutata:  { label: "Rifiutata",  className: "bg-rose-100 text-rose-800 border-rose-200" },
  annullata:  { label: "Annullata",  className: "bg-slate-100 text-slate-700 border-slate-200" },
};

const emptyForm = () => ({
  lavoratore_id: "",
  tipo: "ferie",
  data_inizio: new Date().toISOString().slice(0, 10),
  data_fine: new Date().toISOString().slice(0, 10),
  ore: 0,
  motivazione: "",
});

export default function Ferie() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [lavoratori, setLavoratori] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const [residui, setResidui] = useState([]);
  const [selectedLav, setSelectedLav] = useState("");  // for admin filter
  const [statoFilter, setStatoFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [reviewModal, setReviewModal] = useState(null); // {richiesta, action}
  const [adjModal, setAdjModal] = useState(null); // { residuo }

  const load = useCallback(async () => {
    try {
      const requests = [api.get("/ferie")];
      if (admin) {
        requests.push(api.get("/lavoratori"));
        requests.push(api.get("/ferie/residui"));
      } else {
        // Non-admin: try to load the linked lavoratore + own residui
        requests.push(api.get("/lavoratori").catch(() => ({ data: [] })));
      }
      const [rich, lav, res] = await Promise.all(requests);
      setRichieste(rich.data);
      setLavoratori(lav?.data || []);
      if (admin) setResidui(res?.data || []);
      else {
        // Non-admin: compute own residui if a linked lavoratore exists
        const mine = (lav?.data || []).find((l) => l.user_id === user.user_id);
        if (mine) {
          const r = await api.get(`/ferie/residui?lavoratore_id=${mine.lavoratore_id}`);
          setResidui([r.data]);
        } else {
          setResidui([]);
        }
      }
    } catch (e) {
      toast.error("Errore nel caricamento");
    }
  }, [admin, user.user_id]);

  useEffect(() => { load(); }, [load]);

  const filteredRichieste = useMemo(() => {
    let list = richieste;
    if (selectedLav) list = list.filter((r) => r.lavoratore_id === selectedLav);
    if (statoFilter !== "all") list = list.filter((r) => r.stato === statoFilter);
    return list;
  }, [richieste, selectedLav, statoFilter]);

  const lavName = useCallback((id) => {
    const l = lavoratori.find((x) => x.lavoratore_id === id);
    return l ? `${l.nome} ${l.cognome}` : id;
  }, [lavoratori]);

  const openNew = () => {
    let lav_default = "";
    if (!admin) {
      const mine = lavoratori.find((l) => l.user_id === user.user_id);
      lav_default = mine?.lavoratore_id || "";
    }
    setForm({ ...emptyForm(), lavoratore_id: lav_default });
    setModalOpen(true);
  };

  const submitNew = async () => {
    if (!form.lavoratore_id) {
      toast.error("Seleziona il lavoratore");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        lavoratore_id: form.lavoratore_id,
        tipo: form.tipo,
        data_inizio: `${form.data_inizio}T00:00:00`,
        data_fine: `${form.data_fine}T23:59:59`,
        ore: Number(form.ore) || 0,
        motivazione: form.motivazione,
      };
      await api.post("/ferie", payload);
      toast.success("Richiesta inviata, in attesa di approvazione");
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSaving(false);
    }
  };

  const review = async (richiesta, stato, note = "") => {
    try {
      await api.post(`/ferie/${richiesta.richiesta_id}/review`, { stato, note_admin: note });
      toast.success(stato === "approvata" ? "Richiesta approvata" : "Richiesta rifiutata");
      setReviewModal(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Eliminare la richiesta?")) return;
    try {
      await api.delete(`/ferie/${r.richiesta_id}`);
      toast.success("Richiesta eliminata");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-[1400px] mx-auto" data-testid="page-ferie">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-1">
            Ethos · HR
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-black text-slate-900">
            Ferie & Permessi
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {admin
              ? "Approva o rifiuta le richieste. Le richieste approvate finiscono in automatico sul calendario."
              : "Richiedi ferie o permessi. Riceverai risposta dall'amministrazione."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {admin && (
            <>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/ferie/excel`}
                 data-testid="ferie-export-excel"
                 className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50">
                <FileDown className="w-4 h-4" /> Excel
              </a>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/export/ferie/pdf`}
                 data-testid="ferie-export-pdf"
                 className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-sm hover:bg-slate-50">
                <FileText className="w-4 h-4" /> PDF
              </a>
            </>
          )}
          <button
            onClick={openNew}
            data-testid="btn-new-richiesta"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2 rounded-sm shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Nuova richiesta
          </button>
        </div>
      </div>

      {/* Contatori residui */}
      {residui.length > 0 && (
        <div className="grid gap-3 mb-6" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
          {residui.map((r) => (
            <ResiduiCard
              key={r.lavoratore_id}
              data={r}
              nome={lavName(r.lavoratore_id)}
              admin={admin}
              onManageAdjustments={(d) => setAdjModal({ residuo: d })}
            />
          ))}
        </div>
      )}

      {/* Filters */}
      {admin && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select
            value={selectedLav}
            onChange={(e) => setSelectedLav(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
            data-testid="filter-lavoratore"
          >
            <option value="">Tutti i lavoratori</option>
            {lavoratori.map((l) => (
              <option key={l.lavoratore_id} value={l.lavoratore_id}>
                {l.nome} {l.cognome}
              </option>
            ))}
          </select>
          <div className="flex gap-1">
            {["all", "in_attesa", "approvata", "rifiutata"].map((s) => (
              <button
                key={s}
                onClick={() => setStatoFilter(s)}
                className={`px-3 py-1.5 text-xs font-semibold border rounded-full ${
                  statoFilter === s
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                }`}
              >
                {s === "all" ? "Tutte" : STATO_META[s]?.label || s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Lista richieste */}
      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-3 py-2">Tipo</th>
              {admin && <th className="px-3 py-2">Lavoratore</th>}
              <th className="px-3 py-2">Dal</th>
              <th className="px-3 py-2">Al</th>
              <th className="px-3 py-2">Giorni/Ore</th>
              <th className="px-3 py-2">Motivazione</th>
              <th className="px-3 py-2">Stato</th>
              <th className="px-3 py-2 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredRichieste.length === 0 && (
              <tr>
                <td colSpan={admin ? 8 : 7} className="px-3 py-8 text-center text-slate-500">
                  Nessuna richiesta.
                </td>
              </tr>
            )}
            {filteredRichieste.map((r) => {
              const meta = TIPO_META[r.tipo] || TIPO_META.ferie;
              const Icon = meta.icon;
              const stato = STATO_META[r.stato] || {};
              return (
                <tr key={r.richiesta_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`richiesta-${r.richiesta_id}`}>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: meta.color }}>
                      <Icon className="w-4 h-4" /> {meta.label}
                    </span>
                  </td>
                  {admin && <td className="px-3 py-3 font-medium">{lavName(r.lavoratore_id)}</td>}
                  <td className="px-3 py-3">{new Date(r.data_inizio).toLocaleDateString("it-IT")}</td>
                  <td className="px-3 py-3">{new Date(r.data_fine).toLocaleDateString("it-IT")}</td>
                  <td className="px-3 py-3 font-medium">
                    {r.tipo === "permesso" ? `${r.ore || 0} ore` : `${r.giorni || 0} gg`}
                  </td>
                  <td className="px-3 py-3 text-slate-600 max-w-xs truncate" title={r.motivazione}>
                    {r.motivazione || "—"}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded-full border ${stato.className || ""}`}>
                      {stato.label || r.stato}
                    </span>
                    {r.note_admin && r.stato === "rifiutata" && (
                      <div className="text-[10px] text-slate-500 mt-0.5">{r.note_admin}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {admin && r.stato === "in_attesa" && (
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => review(r, "approvata")}
                          data-testid={`approve-${r.richiesta_id}`}
                          className="inline-flex items-center gap-1 text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded text-xs font-semibold"
                        >
                          <ThumbsUp className="w-3.5 h-3.5" /> Approva
                        </button>
                        <button
                          onClick={() => setReviewModal({ richiesta: r, action: "rifiutata" })}
                          data-testid={`reject-${r.richiesta_id}`}
                          className="inline-flex items-center gap-1 text-rose-700 hover:bg-rose-50 px-2 py-1 rounded text-xs font-semibold"
                        >
                          <ThumbsDown className="w-3.5 h-3.5" /> Rifiuta
                        </button>
                      </div>
                    )}
                    {(admin || r.stato === "in_attesa") && (
                      <button
                        onClick={() => remove(r)}
                        className="inline-flex items-center text-slate-400 hover:text-rose-700 p-1 ml-1"
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* New request modal */}
      {modalOpen && (
        <NewRequestModal
          form={form}
          setForm={setForm}
          lavoratori={admin ? lavoratori : lavoratori.filter((l) => l.user_id === user.user_id)}
          admin={admin}
          onClose={() => setModalOpen(false)}
          onSubmit={submitNew}
          saving={saving}
        />
      )}

      {/* Reject reason modal */}
      {reviewModal && (
        <RejectModal
          richiesta={reviewModal.richiesta}
          onClose={() => setReviewModal(null)}
          onConfirm={(note) => review(reviewModal.richiesta, "rifiutata", note)}
        />
      )}

      {/* Modifica saldo (aggiustamenti + riporto) */}
      {adjModal && admin && (
        <AdjustmentsModal
          residuo={adjModal.residuo}
          onClose={() => setAdjModal(null)}
          onSaved={async () => {
            setAdjModal(null);
            await load();
          }}
        />
      )}
    </div>
  );
}
